import { compareAsc, format, isSameYear } from "date-fns";
import { toTitleCase } from "../utils/string";

// For supporting backward compatibility
function getTemporalLabelForRecurringChannelType_Deprecated(planType) {
  let temporalLabel;

  switch (planType) {
    case "per_week": {
      temporalLabel = "Weekly";
      break;
    }
    case "per_month": {
      temporalLabel = "Monthly";
      break;
    }
    case "every_2_months": {
      temporalLabel = "2-Months";
      break;
    }
    case "every_3_months": {
      temporalLabel = "3-Months";
      break;
    }
    case "every_6_months": {
      temporalLabel = "6-Months";
      break;
    }
    case "every_12_months": {
      temporalLabel = "12-Months";
      break;
    }
  }
  return temporalLabel;
}

export function getTemporalLabelForRecurringChannelType(planDetails) {
  const { planType, interval, duration } = planDetails; // Note: planType is recurring_type
  let temporalLabel;

  const isLifetimePlan = duration === "lifetime";
  const isCustomPlan = planType === "custom";

  if(isLifetimePlan) {
    temporalLabel = "Lifetime";
  } else if (!isCustomPlan) {
    temporalLabel =
      getTemporalLabelForRecurringChannelType_Deprecated(planType); //Note: planType is recurring_type
  } else {
    if (interval == 1) {
      if (duration === "days") {
        temporalLabel = "Daily";
      } else if (duration === "months") {
        temporalLabel = "Monthly";
      }
    } else {
      temporalLabel = `${interval}-${toTitleCase(duration)}`;
    }
  }

  return temporalLabel;
}

function getTemporalLabelForMultiDayChannel({ start_date, end_date }) {
  let temporalLabel;

  let startDate = format(new Date(start_date), "dd MMM");

  if (end_date) {
    let endDate = format(new Date(end_date), "dd MMM");

    const isStartDateThisYear = isSameYear(new Date(start_date), new Date());
    const isEndDateThisYear = isSameYear(new Date(end_date), new Date());

    if (!isStartDateThisYear || !isEndDateThisYear) {
      startDate = format(new Date(start_date), "dd MMM ''yy");
      endDate = format(new Date(end_date), "dd MMM ''yy");
    }

    if (startDate === endDate) {
      temporalLabel = startDate;
    } else {
      temporalLabel = `${startDate} - ${endDate}`;
    }
  } else {
    temporalLabel = startDate;
  }

  return temporalLabel;
}

export function getTemporalLabel(
  {
    channelType, // Note: channelType is schedule_type
    planDetails,
    start_date,
    end_date,
  },
  isLabelWithHyphen = true
) {
  let temporalLabel;

  switch (channelType) {
    case "recurring": {
      temporalLabel = getTemporalLabelForRecurringChannelType(planDetails);
      break;
    }
    case "multi_day_session": {
      temporalLabel = getTemporalLabelForMultiDayChannel({
        start_date,
        end_date,
      });
      break;
    }
    case "one_time": {
      const startDate = format(new Date(start_date), "d MMM, hh:mma");
      temporalLabel = startDate;

      break;
    }
  }

  return isLabelWithHyphen ? temporalLabel : temporalLabel.replace("-", " ");
}

export function getRecurringChannelStartDate(startDate) {
  let recurringStartDate = format(new Date(startDate), "d MMM");
  const nowDate = new Date();
  const isStartDateThisYear = isSameYear(new Date(startDate), nowDate);

  if (!isStartDateThisYear) {
    recurringStartDate = format(new Date(startDate), "dd MMM ''yy");
  }

  const recurringDatePlaceholder =
    compareAsc(nowDate, new Date(startDate)) === -1
      ? "Starting on " + recurringStartDate
      : "";

  return recurringDatePlaceholder;
}

function getSeatStatusLabel(seatThreshold, total_seats_left, total_seats) {
  const noSeatsSold = total_seats_left === total_seats;
  const seatsLeftSuffix = total_seats_left === 1 ? "seat" : "seats";

  if (noSeatsSold) {
    return `Only ${total_seats} ${seatsLeftSuffix} available`;
  } else if (seatThreshold <= 10 || total_seats_left <= seatThreshold) {
    return `Only ${total_seats_left} ${seatsLeftSuffix} left`;
  } else {
    return `Limited seats available`;
  }
}

export function calcSeatStatus(total_seats, total_seats_left) {
  let seatStatus = "Limited seats available";
  let thresholdPercetage;

  if (!total_seats || !total_seats_left) {
    return "No seats left";
  }

  if (total_seats <= 100) {
    thresholdPercetage = 0.2;
    const seatThreshold = Math.ceil(total_seats * thresholdPercetage);

    seatStatus = getSeatStatusLabel(
      seatThreshold,
      total_seats_left,
      total_seats
    );
  } else if (total_seats > 100 && total_seats <= 500) {
    thresholdPercetage = 0.15;
    const seatThreshold = Math.ceil(total_seats * thresholdPercetage);

    seatStatus = getSeatStatusLabel(
      seatThreshold,
      total_seats_left,
      total_seats
    );
  } else if (total_seats > 500) {
    thresholdPercetage = 0.1;
    const seatThreshold = Math.ceil(total_seats * thresholdPercetage);

    seatStatus = getSeatStatusLabel(
      seatThreshold,
      total_seats_left,
      total_seats
    );
  }

  return seatStatus;
}

export function getChannelTypeFromMetaInfo(type) {
  if (type === "recurring") {
    return "Channel";
  }

  if (type === "one_time") {
    return "Session";
  }

  if (type === "multi_day_session") {
    return "Event";
  }

  return "";
}

export function getShareMessageForChannel(currentUsername, channelDetails) {
  const url = channelDetails.short_url_flutter || channelDetails.short_url;
  let message = "";

  // If creator is sharing his own channel
  if (currentUsername === channelDetails.creator.username) {
    if (channelDetails.type === "recurring") {
      message = `I invite you to subscribe my channel on qoohoo "${channelDetails.name}"`;
    } else if (channelDetails.type === "free_subscription") {
      message = `I invite you to subscribe to my free channel on qoohoo "${channelDetails.name}"`;
    } else {
      const channelType =
        channelDetails.type === "multi_day_session" ? "session" : "event";

      message = `I invite you to join my ${channelType} on qoohoo "${channelDetails.name}"`;
    }
  } else {
    const creatorName =
      channelDetails.creator.fullname || channelDetails.creator.username;
    const channelType =
      channelDetails.type === "multi_day_session" ? "a session" : "an event";

    // If member is sharing a creator channel
    if (channelDetails.type === "recurring") {
      message = `Subscribe to channel "${channelDetails.name}" by ${creatorName} on qoohoo!`;
    } else if (channelDetails.type === "free_subscription") {
      message = `Subscribe to free channel "${channelDetails.name}" by ${creatorName} on qoohoo!`;
    } else {
      message = `${creatorName} is hosting ${channelType} "${channelDetails.name}" on qoohoo!`;
    }
  }

  return [message, url];
}

export const isFinanceChannel = (categories, title, description) => {
  let _isFinanceChannel = false;
  const _title = title?.toLowerCase();
  const _description = description?.toLowerCase();
  const keywords = ["trade", "trading", "nifty", "sensex", "calls", "jackpot", "stock", "crypto", "bitcoin"];
  
  const category = categories?.find((category) => {
    return category?.display_name?.toLowerCase() === "finance";
  })

  if(category) {
    _isFinanceChannel = true;
  } else {
    for(const keyword of keywords) {
      const isTitleAFinanceCategory = _title?.indexOf(keyword) > -1;
      const isFinanceDescription = _description?.indexOf(keyword) > -1;
      
      if(isTitleAFinanceCategory || isFinanceDescription) {
        _isFinanceChannel = true;
        break;
      }
    }
  }

  return _isFinanceChannel;
}

export function shouldShowStrikethroughAmount(amount, amount_after_discount) {
  let _shouldShowStrikethroughAmount = false;
  
  if(!!amount_after_discount) {
    _shouldShowStrikethroughAmount = true;
  }

  if(amount_after_discount == amount) {
    _shouldShowStrikethroughAmount = false
  }
  
  return _shouldShowStrikethroughAmount;
}